import { useMemo } from 'react'
import { useAuth } from '@structural-climbing/auth'
import { useCompetitions } from '../competitions/useCompetitions'
import type { CompetitionSummary } from '../competitions/types'
import { useNews } from '../news/useNews'
import { useSeenNews } from '../../context/SeenNewsContext'
import { splitFeed } from '../news/newsSelectors'
import { isUpcoming, sortNews } from '../news/sortNews'
import type { NewsItem } from '@structural-climbing/ui'

/** How many of each kind Home shows before "see all" takes over. */
export const HOME_NEWS_LIMIT = 2

export interface HomeNews {
  competitions: CompetitionSummary[]
  events: NewsItem[]
  news: NewsItem[]
  /** Anything to show at all: decides whether the Noutăți chip exists. */
  hasAny: boolean
  /** Something running, coming up or unread: decides the chip's dot. */
  hasNew: boolean
  joinEvent: ReturnType<typeof useNews>['joinEvent']
  leaveEvent: ReturnType<typeof useNews>['leaveEvent']
}

/**
 * What is going on at the gym, for Home's Noutăți tab: competitions not over
 * yet, the gym's upcoming events, and news you have not read.
 *
 * Every list comes from a request the app already makes (the tab bar's dots
 * read the same news, the competitions teaser the same competitions), so this
 * adds nothing to the page's calls.
 */
export function useHomeNews(gymId: string): HomeNews {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { competitions: all } = useCompetitions(gymId, apiUrl, getAccessTokenSilently)
  const { gym, global, joinEvent, leaveEvent } = useNews(gymId)
  const { seenNews } = useSeenNews()

  return useMemo(() => {
    const competitions = all
      .filter((c) => c.state !== 'ended')
      // Running first, then the soonest.
      .sort((a, b) =>
        a.state === b.state
          ? a.starts_at.localeCompare(b.starts_at)
          : a.state === 'in_progress'
            ? -1
            : 1,
      )
      .slice(0, HOME_NEWS_LIMIT)
    const { gymEvents, gymNews, communityNews } = splitFeed({ gym, global })
    const events = sortNews(gymEvents.filter((e) => isUpcoming(e))).slice(0, HOME_NEWS_LIMIT)
    const news = sortNews([...gymNews, ...communityNews].filter((n) => !seenNews.has(n.id))).slice(
      0,
      HOME_NEWS_LIMIT,
    )
    const hasAny = competitions.length + events.length + news.length > 0
    return { competitions, events, news, hasAny, hasNew: hasAny, joinEvent, leaveEvent }
  }, [all, gym, global, seenNews, joinEvent, leaveEvent])
}
